// import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import {
  ArrayElement,
  LayoutContent as baseLayoutText,
  getLocalizedContent,
} from "@onzeit/shared";
import { Form, useFetcher } from "@remix-run/react";
import clsx from "clsx";
import { PropsWithoutRef } from "react";
import { Layout } from "../index";

type NewsletterFormProps = {
  text: ArrayElement<typeof baseLayoutText>;
  Form: any;
  locale: string;
  errors: string[] | undefined;
  success: boolean | undefined;
};

export const NewsletterForm = (props: PropsWithoutRef<NewsletterFormProps>) => {
  const layoutText = getLocalizedContent(props.locale, baseLayoutText);
  const fetcher = useFetcher<{
    success: boolean;
    errors: string[];
  }>();
  return (
    <>
      <>
        {!fetcher.data?.success && (
          <fetcher.Form
            className={clsx("mt-6", props.success ? "hidden" : "block")}
            method="post"
            aria-hidden={props.success}
            action={`/${props.locale}/newsletter`}
          >
            <p className="my-4 text-sm leading-6 text-gray-600">
              {props.text.data.newsletterSubtext}
            </p>
            <div className="flex w-full max-w-md">
              <input
                type="email"
                name="email-address"
                id="email-address"
                autoComplete="email"
                required
                disabled={fetcher.state === "submitting"}
                className="min-w-0 appearance-none rounded-md border-0 bg-white px-3 py-1.5 text-base text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:w-64 sm:text-sm sm:leading-6 xl:w-full"
                placeholder={props.text.data.newsletterPlaceholder}
              />
              <button
                type="submit"
                disabled={fetcher.state === "submitting"}
                className="ml-2 flex rounded-md bg-indigo-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 disabled:bg-indigo-300"
              >
                {fetcher.state === "submitting" && (
                  <div role="status" className="flex items-center">
                    <svg
                      aria-hidden="true"
                      className="mr-2 mt-[2px] h-4 w-4 animate-spin fill-indigo-100 text-indigo-400"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                    <span className="sr-only">Loading...</span>
                  </div>
                )}
                {props.text.data.newsletterButtonText}
              </button>
            </div>
          </fetcher.Form>
        )}
        {fetcher.data?.success && (
          <div className="mt-8">
            <ThankYou {...props} />
          </div>
        )}
        {fetcher.data?.errors && (
          <div className="col-start-2 text-lg">
            {fetcher.data.errors.map((error) => (
              <div key={error}>{error}</div>
            ))}
          </div>
        )}
      </>
      {props.success === true && (
        <Layout Form={Form} text={layoutText} locale={props.locale}>
          <ThankYou {...props} />
        </Layout>
      )}
    </>
  );
};

const ThankYou = (props: PropsWithoutRef<NewsletterFormProps>) => {
  return (
    <>
      <div className="grid w-full grid-cols-[32px_1fr] grid-rows-[auto_auto_auto] items-start rounded-md bg-indigo-600 px-4 py-2 text-left drop-shadow-lg">
        <div className="mt-[2px] h-6">
          <CheckCircleIcon className="mr-2 h-6 w-6 text-indigo-50" />
        </div>
        <div className="text-xl font-semibold text-indigo-50">
          {props.text.data.newsletterThankYouText}
        </div>
        <div className="col-start-2 text-lg text-indigo-200">
          {props.text.data.newsletterThankYouSubtext}
        </div>
      </div>
    </>
  );
};
